import { useContext, useCallback, useEffect } from 'react';
import type { ApolloError } from 'apollo-client';

import { ExperienceTrackerContext } from '@confluence/experience-tracker';
import {
	Attribution,
	getStatusCodeError,
	isTooManyRequestsError,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { getMonitoringClient } from '@confluence/monitoring';

// Expected ones are 403 and 429s. Unexpected errors include 400, 404 and 500.
const isExpectedError = (error: ApolloError) =>
	isUnauthorizedError(error) || isTooManyRequestsError(error);

export const useAnalyticsExperienceTracking = ({
	experienceName,
	abortOnUnmount = false,
}: {
	experienceName: string;
	abortOnUnmount?: boolean;
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	// Abort the given experience when the container component unmounts
	useEffect(() => {
		return () => {
			if (abortOnUnmount) {
				experienceTracker.abort({
					name: experienceName,
					reason: 'useAnalyticsExperienceTracking unmounted',
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Use the common error handler for any initial/loading experience.
	// For interactive, update based experiences (i.e. filters) we can fallback to "submitError" or create individual experiences.
	const handleAnalyticsError = useCallback(
		(error?: ApolloError) => {
			if (!error) {
				return;
			}

			// Errors coming from hooks like usePageAudience could have been marked already
			if (!isErrorMarkedAsHandled(error)) {
				markErrorAsHandled(error);
			}

			if (isExpectedError(error)) {
				experienceTracker.abort({
					name: experienceName,
					reason: `Received ${getStatusCodeError(error)} with the message ${error.message}`,
				});
				return;
			}

			// At this point, the experience might have already stopped since users can change filters interactively. To log their errors, we use submitError as a last resort.
			const expState = experienceTracker.getExperienceState(experienceName);

			if (!Boolean(expState?.hasStopped)) {
				experienceTracker.fail({
					name: experienceName,
					error,
				});
			} else {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.CC_INSIGHTS_AND_REPORTING,
				});
			}
		},
		[experienceName, experienceTracker],
	);

	return { handleAnalyticsError };
};
